import {
  useGetDevices,
  useGetOrgTasks,
  useGetUser,
  useGlobalDeviceContext,
} from "@hooks";
import { AddRounded } from "@mui/icons-material";
import {
  Button,
  ListSubheader,
  MenuItem,
  Skeleton,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { PageHeader } from "../components";
import { CreateTaskModal } from "../components/Tasks/CreateTaskModal";
import { TaskList } from "../components/Tasks/TaskList";
import { CompactSelect } from "../components/ui/CompactSelect";
import { TaskScope, UnifiedTask } from "../services";
import { isStatusComplete } from "../utils/documents";

type TaskCompletionFilter = "incomplete" | "all";

const isCompletedTask = (task: UnifiedTask) => {
  if (task.taskType === "DOCUMENT") {
    return Boolean(isStatusComplete(task.docStatus ?? null));
  }

  return task.status === "DONE";
};

function Tasks() {
  const { orgId = "" } = useParams<{ orgId: string }>();
  const { data: user } = useGetUser();
  const { activeGlobalDeviceId, setActiveGlobalDeviceId } =
    useGlobalDeviceContext();
  const canCreateTask = !!user?.permissions?.includes("write:configuration");
  const { data: devices = [] } = useGetDevices({ orgId });
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedDeviceId = searchParams.get("deviceId") ?? "";
  const selectedDevice =
    devices.find((device) => device.id === selectedDeviceId) ?? null;
  const [scope, setScope] = useState<TaskScope>("mine");
  const [completionFilter, setCompletionFilter] =
    useState<TaskCompletionFilter>("incomplete");
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const {
    data: tasks,
    isLoading,
    error,
  } = useGetOrgTasks({
    orgId,
    scope,
    enabled: !!orgId,
  });

  const filteredTasks = useMemo(() => {
    if (!tasks) {
      return [];
    }
    return tasks.filter((task) => {
      const matchesDevice =
        !selectedDeviceId ||
        task.device?.id === selectedDeviceId ||
        task.device === null;
      const matchesCompletionFilter =
        completionFilter === "all" || !isCompletedTask(task);

      return matchesDevice && matchesCompletionFilter;
    });
  }, [completionFilter, tasks, selectedDeviceId]);

  useEffect(() => {
    if (!selectedDeviceId) {
      return;
    }

    setActiveGlobalDeviceId(selectedDeviceId);
  }, [selectedDeviceId, setActiveGlobalDeviceId]);

  const fallbackDeviceId = useMemo(() => {
    if (selectedDeviceId) {
      return selectedDeviceId;
    }
    if (activeGlobalDeviceId) {
      return activeGlobalDeviceId;
    }
    if (devices.length === 0) {
      return "";
    }
    const newestDevice = devices
      .slice()
      .sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      )[0];
    return newestDevice?.id ?? "";
  }, [activeGlobalDeviceId, devices, selectedDeviceId]);

  const handleDeviceSelect = (nextDeviceId: string) => {
    const nextParams = new URLSearchParams(searchParams);
    if (nextDeviceId === "all") {
      nextParams.delete("deviceId");
    } else {
      nextParams.set("deviceId", nextDeviceId);
      setActiveGlobalDeviceId(nextDeviceId);
    }
    setSearchParams(nextParams);
  };

  const handleScopeChange = (nextScope: string) => {
    if (nextScope === "mine" || nextScope === "all") {
      setScope(nextScope);
    }
  };

  const handleCompletionFilterChange = (nextFilter: string) => {
    if (nextFilter === "incomplete" || nextFilter === "all") {
      setCompletionFilter(nextFilter);
    }
  };

  const scopeLabel = scope === "mine" ? "Current user" : "All users";
  const completionFilterLabel =
    completionFilter === "incomplete" ? "Incomplete Tasks" : "All Tasks";
  const deviceLabel = selectedDevice?.name || "All Devices";
  const selectedDeviceValue = selectedDeviceId || "all";

  if (!user) {
    return <TasksSkeleton />;
  }

  return (
    <>
      <div className="flex flex-1 h-full flex-col p-0 gap-y-3">
        <PageHeader
          pageTitleComponent={<Typography>Tasks</Typography>}
          rightPanelToggleRequiresDevice={false}
          rightButtons={
            <div className="flex items-center gap-3">
              {devices.length > 0 && (
                <CompactSelect
                  value={selectedDeviceValue}
                  onChange={handleDeviceSelect}
                  ariaLabel="Device filter"
                  renderValue={() => deviceLabel}
                >
                  <ListSubheader>Show tasks for</ListSubheader>
                  <MenuItem value="all">All Devices</MenuItem>
                  {devices.map((device) => (
                    <MenuItem key={device.id} value={device.id}>
                      {device.name}
                    </MenuItem>
                  ))}
                </CompactSelect>
              )}
              <CompactSelect
                value={scope}
                onChange={handleScopeChange}
                ariaLabel="Task scope"
                renderValue={() => scopeLabel}
              >
                <ListSubheader>Filter tasks by</ListSubheader>
                <MenuItem value="mine">Current user</MenuItem>
                <MenuItem value="all">All users</MenuItem>
              </CompactSelect>
              <CompactSelect
                value={completionFilter}
                onChange={handleCompletionFilterChange}
                ariaLabel="Task status filter"
                renderValue={() => completionFilterLabel}
              >
                <ListSubheader>Task status</ListSubheader>
                <MenuItem value="incomplete">Incomplete Tasks</MenuItem>
                <MenuItem value="all">All Tasks</MenuItem>
              </CompactSelect>
              {canCreateTask && (
                <Button
                  variant="text"
                  size="small"
                  color="inherit"
                  startIcon={<AddRounded />}
                  onClick={() => setCreateModalOpen(true)}
                >
                  Create Task
                </Button>
              )}
            </div>
          }
        />

        {isLoading && <TasksListSkeleton />}

        {error && (
          <div className="flex flex-1 flex-col items-center justify-center">
            <Typography color="error">
              Failed to load tasks. Please try again.
            </Typography>
          </div>
        )}

        {!isLoading && !error && filteredTasks.length === 0 && (
          <div className="flex flex-1 flex-col items-center justify-center text-center gap-4">
            <Typography variant="body1" color="text.secondary">
              {scope === "mine"
                ? selectedDeviceId
                  ? "No tasks assigned to you for this device yet."
                  : "No tasks assigned to you yet."
                : selectedDeviceId
                  ? "No tasks for this device yet."
                  : "No tasks in this organization yet."}
            </Typography>
            {canCreateTask && (
              <Button
                variant="contained"
                startIcon={<AddRounded />}
                onClick={() => setCreateModalOpen(true)}
              >
                Create Task
              </Button>
            )}
          </div>
        )}

        {!isLoading && !error && filteredTasks.length > 0 && (
          <TaskList
            tasks={filteredTasks}
            highlightedDeviceId={selectedDeviceId || undefined}
            fallbackDeviceId={fallbackDeviceId || undefined}
          />
        )}
      </div>

      {canCreateTask && (
        <CreateTaskModal
          open={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          orgId={orgId}
          defaultDeviceId={selectedDeviceId || undefined}
        />
      )}
    </>
  );
}

function TasksSkeleton() {
  return (
    <div className="flex flex-1 h-full flex-col p-0 gap-y-3">
      <div className="flex justify-between items-center">
        <Skeleton variant="text" width={100} height={40} />
        <Skeleton variant="rectangular" width={200} height={36} />
      </div>
      <TasksListSkeleton />
    </div>
  );
}

function TasksListSkeleton() {
  const skeletonRowIds = [
    "task-skeleton-1",
    "task-skeleton-2",
    "task-skeleton-3",
    "task-skeleton-4",
    "task-skeleton-5",
  ];

  return (
    <div className="flex flex-col gap-2 py-2 px-3">
      {skeletonRowIds.map((rowId) => (
        <Skeleton key={rowId} variant="rectangular" height={72} />
      ))}
    </div>
  );
}

export default Tasks;
